import * as browser_detect from "@flow/lib/browser_detect";
import { Text } from "@flow/lib/i18n";
import { Link, Tooltip, tooltipClasses } from "@mui/material";
import * as React from "react";
import { memberTheme } from "../memberTheme";
import { TextArea } from "./TextArea";

/*
 * Some pure ugliness to deal with email-misspelled being IE11-incompatible
 *
 * IE doesn't even like you importing this lib.
 */
const emailMisspelled = browser_detect.isIE ? undefined : require("email-misspelled");
function getEmailCorrectionSuggestions(email: string): string[] {
  if (browser_detect.isIE) {
    return [];
  } else {
    return emailMisspelled
      .default({ domains: [...emailMisspelled.top100, "gmail.co.uk", "googlemail.co.uk", "btinternet.com"] })(email)
      .map((r: any) => r.corrected);
  }
}

//A very basic email regex, just want to make sure that members are entering something
// that looks vaguely like an email address
// Has only one @, at least one character before the @, before the period and after it:
const basicEmailPattern = "^[^@]+@[^@]+.[^@]+$";
const basicEmailRegex = new RegExp(basicEmailPattern);

export function EmailInput(props: {
  value?: string;
  onChange: (v: string) => void;
  label?: string;
  inputLabelShrink?: boolean;
  // SW-200 - Extended to handle the temporary isEmailOptional control.
  isEmailOptional?: boolean; // Optional boolean to allow for cases where we don't want to capture an email.
}) {
  const [suggestedEmail, setSuggestedEmail] = React.useState<undefined | string>(undefined);

  const validate = (email: string) => {
    const suggestions = getEmailCorrectionSuggestions(email);
    if (suggestions.length > 0) {
      setSuggestedEmail(suggestions[0]);
    } else {
      setSuggestedEmail(undefined);
    }

    const emailPresent = email.trim() !== "";
    const emailValidish = basicEmailRegex.test(email.trim().toLowerCase());

    return (!email && props.isEmailOptional) || (emailPresent && emailValidish);
  };

  const renderSuggestedEmail = () => {
    if (suggestedEmail) {
      return (
        <Text
          id="plugin.petition.suggestedEmail"
          fields={{
            email: suggestedEmail,
          }}
        >
          Did you mean{" "}
          <Link
            sx={{
              textDecoration: "underline",
              "&:hover": {
                color: memberTheme.palette.primary.contrastText,
                cursor: "pointer",
                textDecoration: "underline",
              },
            }}
            underline="always"
            color={memberTheme.palette.primary.contrastText}
            onClick={(e) => {
              e.preventDefault();
              props.onChange(suggestedEmail);
              setSuggestedEmail(undefined); // clear the warning
            }}
          >
            {suggestedEmail}?
          </Link>
        </Text>
      );
    }
    return <></>;
  };

  return (
    <>
      <Tooltip
        title={renderSuggestedEmail()}
        open={suggestedEmail !== undefined}
        arrow
        placement="top"
        PopperProps={{
          sx: {
            [`& .${tooltipClasses.tooltip}`]: {
              boxShadow: memberTheme.shadows[1],
              fontSize: "1rem",
            },
          },
        }}
      >
        <TextArea
          id="member-email"
          required={!props.isEmailOptional}
          onChange={props.onChange}
          validateInput={validate}
          value={props.value}
          inputPattern={basicEmailPattern}
          errorFeedbackText="Please enter a valid email address"
          label={props.label || "Email"}
          inputLabelShrink={props.inputLabelShrink}
        />
      </Tooltip>
    </>
  );
}
